import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import { Button } from "../Button";
import userDeviceStore from "../../../store/device.store";
import { AccessMode } from "../../../models/constants/accessMode";
import { copyToClipboard } from "../../../utils/clipboard";
import { toastService } from "../toast/toast.service";
import localization from "../../../config/localization";

interface PasswordInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | null;
  placeholder?: string;
  showCopyButton?: boolean;
  showPassword?: boolean;
  value?: string;
  isRequired?: boolean;
  variant?: "default" | "auth" | "modal";
}

export function PasswordInput({
  label,
  variant = "default",
  placeholder,
  error,
  showCopyButton,
  showPassword,
  isRequired,
  value,
  ...props
}: PasswordInputProps) {
  const [show, setShow] = useState(false);
  const [copyButtonClicked, setCopyButtonClicked] = useState(false);

  const handleCopy = async () => {
    let accessMode =
      userDeviceStore.getState().accessModeSource.sourceType ??
      AccessMode.Web;

    await copyToClipboard(accessMode, value, {
      onSuccess: () => {
        setCopyButtonClicked(true);
        toastService.success(localization.password_copied);

        setTimeout(() => {
          setCopyButtonClicked(false);
        }, 5000);
      },
      onError: () => {
        toastService.error(localization.failed_to_copy_password);
      },
    });
  };

  const isCopyMode = showCopyButton;
  const shouldShowEyeButton =
    !isCopyMode && !!value && value.length > 0;

  const inputRightPadding = isCopyMode
    ? "pr-20"
    : shouldShowEyeButton
    ? "pr-12"
    : "pr-4";

  return (
    <div className="mb-4 w-full">
      {label && (
        <label
          className={`block ${
            variant === "modal" ? "text-black" : "text-[#A9A9A9]"
          } mb-1 ml-1 font-medium`}
        >
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative w-full mt-2">
        <input
          {...props}
          type={show || showPassword ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={props.onChange}
          autoComplete="off"
          className={`w-full px-4 ${inputRightPadding} py-2 h-12 rounded-lg transition ${
            variant === "modal" ? "text-black" : "text-white"
          }
            focus:outline-none focus:ring-2 focus:ring-[#333333]
            ${variant === "default" ? "bg-[#000000]" : "bg-[#1D1D1D]"}
            ${variant === "modal" ? "bg-white" : ""}
            border ${
              error ? "border-red-500" : "border-[#333333]"
            }`}
        />
        
        {isCopyMode && (
          <Button
            type="button"
            onClick={handleCopy}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 shadow-none"
          >
            <div className="flex items-center gap-1 bg-black text-white px-2 py-1 rounded max-w-[120px] hover:bg-[#C71B4A99]">
              <FontAwesomeIcon icon={faCopy} className="shrink-0" />
              <span className="truncate text-sm">
                {copyButtonClicked ? "Copied" : "Copy"}
              </span>
            </div>
          </Button>
        )}
        
        {shouldShowEyeButton && (
          <Button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 shadow-none"
          >
            <FontAwesomeIcon
              icon={show ? faEyeSlash : faEye}
              size="lg"
              className={`${
                variant === "modal"
                  ? "text-black"
                  : "text-gray-400"
              } hover:text-[#C71B4A99]`}
            />
          </Button>
        )}
      </div>

      {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
    </div>
  );
}